import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ChevronLeft, ChevronRight, FileText, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_ID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

const STATUS_STYLE = {
  H: 'bg-emerald-100 text-emerald-700 font-bold',
  S: 'bg-blue-100 text-blue-700 font-bold',
  I: 'bg-amber-100 text-amber-700 font-bold',
  A: 'bg-red-100 text-red-700 font-bold',
  TS: 'bg-gray-700 text-white font-bold',
};

export default function RekapAbsensi() {
  const today = new Date();
  const [bulan, setBulan] = useState(today.getMonth() + 1);
  const [tahun, setTahun] = useState(today.getFullYear());
  const [santriList, setSantriList] = useState([]);
  const [halaqahList, setHalaqahList] = useState([]);
  const [absensiData, setAbsensiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHalaqah, setSelectedHalaqah] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const daysInMonth = new Date(tahun, bulan, 0).getDate();
  const dayLabels = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [santriRes, halaqahRes, absensiRes] = await Promise.all([
        axios.get(`${API}/santri`),
        axios.get(`${API}/halaqah`),
        axios.get(`${API}/absensi?bulan=${bulan}&tahun=${tahun}`),
      ]);
      setSantriList(santriRes.data || []);
      setHalaqahList(halaqahRes.data || []);
      setAbsensiData(absensiRes.data || []);
    } catch { toast.error('Gagal memuat data absensi'); }
    finally { setLoading(false); }
  }, [bulan, tahun]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (bulan === 1) { setBulan(12); setTahun(y => y - 1); } else setBulan(b => b - 1); };
  const nextMonth = () => { if (bulan === 12) { setBulan(1); setTahun(y => y + 1); } else setBulan(b => b + 1); };

  // Build matrix: santriId → { day → { sore, malam } }
  const matrix = {};
  absensiData.forEach(a => {
    const day = parseInt(a.tanggal?.split('-')[2] || '0');
    if (!day) return;
    if (!matrix[a.santri_id]) matrix[a.santri_id] = {};
    if (!matrix[a.santri_id][day]) matrix[a.santri_id][day] = {};
    const sesi = a.sesi === 'malam' ? 'malam' : 'sore';
    matrix[a.santri_id][day][sesi] = a.status;
  });

  const filteredSantri = selectedHalaqah
    ? santriList.filter(s => s.halaqah_id === selectedHalaqah)
    : santriList;

  const countStatus = (santriId, status) => {
    // Gunakan matrix (sudah deduplicated per tanggal+sesi) bukan raw record
    const sm = matrix[santriId] || {};
    return Object.values(sm).reduce((count, sessions) =>
      count + Object.values(sessions).filter(s => s === status).length, 0);
  };

  const cellChar = (sm, d) => (sm[d]?.sore || sm[d]?.malam || '');

  const exportPDF = async () => {
    if (filteredSantri.length === 0) { toast.error('Tidak ada data santri'); return; }
    setIsExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const W = 297, H = 210, PAD = 6;

      doc.setFillColor(16, 185, 129);
      doc.rect(0, 0, W, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text('REKAP ABSENSI SANTRI', PAD + 2, 8);
      doc.setFontSize(8); doc.setFont('helvetica', 'normal');
      const hName = selectedHalaqah ? halaqahList.find(h => h.id === selectedHalaqah)?.nama || '' : 'Semua Halaqah';
      doc.text(`${hName}  ·  ${MONTHS_ID[bulan - 1]} ${tahun}`, PAD + 2, 14);
      doc.setTextColor(0, 0, 0);

      const nameW = 40, colTotH = 8, colTotS = 8, colTotI = 8, colTotA = 8, colTotTS = 9;
      const allTotW = colTotH + colTotS + colTotI + colTotA + colTotTS;
      const cellW = Math.max(4.5, Math.min(7, (W - PAD * 2 - nameW - allTotW) / daysInMonth));
      const startX = PAD, headerY = 22, rowH = 5.5;
      const tableW = nameW + cellW * daysInMonth + allTotW;

      doc.setFillColor(240, 253, 244); doc.rect(startX, headerY, tableW, rowH, 'F');
      doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(22, 101, 52);
      doc.text('Nama Santri', startX + 2, headerY + 3.8);
      dayLabels.forEach((d, i) => {
        doc.text(String(d), startX + nameW + i * cellW + cellW / 2, headerY + 3.8, { align: 'center' });
      });
      const tX = startX + nameW + daysInMonth * cellW;
      doc.text('H',  tX + colTotH / 2,                               headerY + 3.8, { align: 'center' });
      doc.text('S',  tX + colTotH + colTotS / 2,                     headerY + 3.8, { align: 'center' });
      doc.text('I',  tX + colTotH + colTotS + colTotI / 2,           headerY + 3.8, { align: 'center' });
      doc.text('A',  tX + colTotH + colTotS + colTotI + colTotA / 2, headerY + 3.8, { align: 'center' });
      doc.text('TS', tX + colTotH + colTotS + colTotI + colTotA + colTotTS / 2, headerY + 3.8, { align: 'center' });
      doc.setDrawColor(209, 250, 229); doc.rect(startX, headerY, tableW, rowH);

      filteredSantri.forEach((santri, ri) => {
        const ry = headerY + rowH + ri * rowH;
        const even = ri % 2 === 0;
        doc.setFillColor(even ? 250 : 255, even ? 253 : 255, even ? 250 : 255);
        doc.rect(startX, ry, tableW, rowH, 'F');
        doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(55, 65, 81);
        const n = santri.name?.length > 22 ? santri.name.substring(0, 20) + '..' : santri.name;
        doc.text(n, startX + 2, ry + 3.8);

        const sm = matrix[santri.id] || {};
        dayLabels.forEach((d, i) => {
          const s = cellChar(sm, d);
          doc.setFont('helvetica', s ? 'bold' : 'normal');
          if (s === 'H') doc.setTextColor(5, 150, 105);
          else if (s === 'S') doc.setTextColor(37, 99, 235);
          else if (s === 'I') doc.setTextColor(217, 119, 6);
          else if (s === 'A') doc.setTextColor(220, 38, 38);
          else doc.setTextColor(200, 200, 200);
          doc.text(s || '-', startX + nameW + i * cellW + cellW / 2, ry + 3.8, { align: 'center' });
        });

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(5, 150, 105);
        doc.text(String(countStatus(santri.id, 'H')),  tX + colTotH / 2,                               ry + 3.8, { align: 'center' });
        doc.setTextColor(37, 99, 235);
        doc.text(String(countStatus(santri.id, 'S')),  tX + colTotH + colTotS / 2,                     ry + 3.8, { align: 'center' });
        doc.setTextColor(217, 119, 6);
        doc.text(String(countStatus(santri.id, 'I')),  tX + colTotH + colTotS + colTotI / 2,           ry + 3.8, { align: 'center' });
        doc.setTextColor(220, 38, 38);
        doc.text(String(countStatus(santri.id, 'A')),  tX + colTotH + colTotS + colTotI + colTotA / 2, ry + 3.8, { align: 'center' });
        doc.setTextColor(75, 85, 99);
        doc.text(String(countStatus(santri.id, 'TS')), tX + colTotH + colTotS + colTotI + colTotA + colTotTS / 2, ry + 3.8, { align: 'center' });

        doc.setDrawColor(230, 230, 230); doc.rect(startX, ry, tableW, rowH);
      });

      const legY = H - 8;
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
      doc.text('Keterangan: H = Hadir · S = Sakit · I = Izin · A = Alfa · TS = Tidak Setor  (tiap sel: Sesi 1/Sesi 2)', PAD + 2, legY);
      doc.text(`Dicetak: ${new Date().toLocaleDateString('id-ID', { day:'numeric', month:'long', year:'numeric' })}`, W - PAD - 2, legY, { align: 'right' });

      doc.save(`Rekap-Absensi-${MONTHS_ID[bulan - 1]}-${tahun}.pdf`);
      toast.success('PDF berhasil diunduh');
    } catch { toast.error('Gagal ekspor PDF'); }
    finally { setIsExporting(false); }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-5 pt-12 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ClipboardList size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white font-playfair">Rekap Absensi</h1>
              <p className="text-xs text-emerald-200">Laporan Bulanan Kehadiran Santri</p>
            </div>
          </div>

          <div className="flex items-center justify-between bg-white/10 rounded-2xl px-3 py-2.5">
            <button data-testid="rekap-prev-month" onClick={prevMonth}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white">
              <ChevronLeft size={18} />
            </button>
            <p className="text-base font-bold text-white" data-testid="rekap-month-label">
              {MONTHS_ID[bulan - 1]} {tahun}
            </p>
            <button data-testid="rekap-next-month" onClick={nextMonth}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <select data-testid="rekap-filter-halaqah" value={selectedHalaqah}
                onChange={e => setSelectedHalaqah(e.target.value)}
                className="w-full px-3 pr-7 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 appearance-none">
                <option value="">Semua Halaqah</option>
                {halaqahList.map(h => <option key={h.id} value={h.id}>{h.nama}</option>)}
              </select>
              <ChevronRight size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none rotate-90" />
            </div>
            <button data-testid="rekap-export-pdf-btn" onClick={exportPDF}
              disabled={isExporting || loading}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold shadow-sm transition-all disabled:opacity-60 flex-shrink-0">
              {isExporting
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><FileText size={14} /> Export PDF</>}
            </button>
          </div>

          {!loading && filteredSantri.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { label: 'Santri',    value: filteredSantri.length, color: 'text-gray-700' },
                { label: 'Hadir',     value: filteredSantri.reduce((s, st) => s + countStatus(st.id, 'H'),  0), color: 'text-emerald-600' },
                { label: 'Sakit',     value: filteredSantri.reduce((s, st) => s + countStatus(st.id, 'S'),  0), color: 'text-blue-600' },
                { label: 'Izin',      value: filteredSantri.reduce((s, st) => s + countStatus(st.id, 'I'),  0), color: 'text-amber-600' },
                { label: 'Alfa',      value: filteredSantri.reduce((s, st) => s + countStatus(st.id, 'A'),  0), color: 'text-red-600' },
                { label: 'Tdk Setor', value: filteredSantri.reduce((s, st) => s + countStatus(st.id, 'TS'), 0), color: 'text-gray-700' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white rounded-xl p-2.5 text-center shadow-sm border border-gray-100">
                  <p className={`text-lg font-bold ${color}`}>{value}</p>
                  <p className="text-[9px] text-gray-400 mt-0.5 leading-tight">{label}</p>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredSantri.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">Tidak ada data santri</div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto" data-testid="rekap-table">
                <table className="w-full border-collapse min-w-max text-center">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="sticky left-0 bg-emerald-50 z-10 px-3 py-2.5 text-left text-[10px] font-bold text-emerald-800 border-r border-emerald-100 min-w-[130px]">
                        Nama Santri
                      </th>
                      {dayLabels.map(d => (
                        <th key={d} className="px-0 py-2.5 text-[10px] font-bold text-emerald-800 w-7 min-w-[28px]">{d}</th>
                      ))}
                      <th className="px-2 py-2.5 text-[10px] font-bold text-emerald-700 bg-emerald-100 border-l border-emerald-200 min-w-[28px]">H</th>
                      <th className="px-2 py-2.5 text-[10px] font-bold text-blue-700 bg-blue-50 min-w-[28px]">S</th>
                      <th className="px-2 py-2.5 text-[10px] font-bold text-amber-700 bg-amber-50 min-w-[28px]">I</th>
                      <th className="px-2 py-2.5 text-[10px] font-bold text-red-700 bg-red-50 min-w-[28px]">A</th>
                      <th className="px-2 py-2.5 text-[10px] font-bold text-gray-600 bg-gray-100 min-w-[30px]">TS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSantri.map((santri, ri) => {
                      const sm = matrix[santri.id] || {};
                      return (
                        <tr key={santri.id} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <td className={`sticky left-0 z-10 px-3 py-2 border-r border-gray-100 text-xs font-medium text-gray-700 text-left ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <span className="truncate block max-w-[110px]">{santri.name}</span>
                          </td>
                          {dayLabels.map(d => {
                            const cell = sm[d] || {};
                            return (
                              <td key={d} className="py-1 px-0.5">
                                <div className="flex flex-col items-center gap-0.5">
                                  {['sore','malam'].map(ss => (
                                    <span key={ss} title={ss === 'sore' ? 'Sesi 1 (Sore)' : 'Sesi 2 (Malam)'}
                                      className={`inline-flex items-center justify-center text-[8px] w-4 h-4 rounded ${cell[ss] ? STATUS_STYLE[cell[ss]] : 'bg-gray-100/70 text-transparent'}`}>
                                      {cell[ss] || '·'}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            );
                          })}
                          <td className="py-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50/50 border-l border-emerald-100">{countStatus(santri.id, 'H') || 0}</td>
                          <td className="py-1.5 text-[11px] font-bold text-blue-600 bg-blue-50/30">{countStatus(santri.id, 'S') || 0}</td>
                          <td className="py-1.5 text-[11px] font-bold text-amber-600 bg-amber-50/30">{countStatus(santri.id, 'I') || 0}</td>
                          <td className="py-1.5 text-[11px] font-bold text-red-600 bg-red-50/30">{countStatus(santri.id, 'A') || 0}</td>
                          <td className="py-1.5 text-[11px] font-bold text-gray-600 bg-gray-50/30">{countStatus(santri.id, 'TS') || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-4 flex-wrap">
                {[['H','Hadir', STATUS_STYLE.H], ['S','Sakit', STATUS_STYLE.S], ['I','Izin', STATUS_STYLE.I], ['A','Alfa', STATUS_STYLE.A], ['TS','Tdk Setor', STATUS_STYLE.TS]].map(([key, label, style]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span className={`text-[10px] w-5 h-5 flex items-center justify-center rounded-md ${style}`}>{key}</span>
                    <span className="text-xs text-gray-500">{label}</span>
                  </div>
                ))}
                <span className="w-full text-[10px] text-gray-400 mt-1">Tiap hari menampilkan 2 kotak: atas = Sesi 1 (Sore), bawah = Sesi 2 (Malam).</span>
                <span className="text-[10px] text-gray-300 ml-auto">{MONTHS_ID[bulan - 1]} {tahun} · {filteredSantri.length} santri</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
